import { jsPDF } from 'jspdf';
import type { ContractProduct } from './ContractDetailsDrawer';
import { formatCurrency, formatPeriodDate } from './format';

export function exportContractToPdf(products: ContractProduct[], dealerName?: string) {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const font = 'helvetica';
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    const colRight = pageW - margin;
    let y = 18;

    // ── Header bar ──
    doc.setFillColor(45, 58, 63);
    doc.rect(0, 0, pageW, 12, 'F');
    doc.setFont(font, 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text('Current Contract Details', margin, 8);
    const date = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    doc.setFont(font, 'normal');
    doc.setFontSize(8);
    doc.text(date, colRight, 8, { align: 'right' });

    // ── Dealer name ──
    if (dealerName) {
        doc.setFont(font, 'bold');
        doc.setFontSize(14);
        doc.setTextColor(35, 47, 52);
        doc.text(dealerName, margin, y);
        y += 7;
    }

    // ── Summary ──
    const totalBudget = products.reduce((sum, p) => sum + p.periods[p.periods.length - 1].grossBudget, 0);
    doc.setFont(font, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(138, 145, 153);
    doc.text('Total Gross Budget', margin, y);
    doc.setFont(font, 'bold');
    doc.setTextColor(35, 47, 52);
    doc.text(formatCurrency(totalBudget), margin + 32, y);
    doc.setFont(font, 'normal');
    doc.setTextColor(138, 145, 153);
    doc.text('Active Products', margin + 65, y);
    doc.setFont(font, 'bold');
    doc.setTextColor(35, 47, 52);
    doc.text(String(products.length), margin + 90, y);
    y += 5;

    // ── Divider ──
    doc.setDrawColor(228, 232, 234);
    doc.setLineWidth(0.4);
    doc.line(margin, y, colRight, y);
    y += 6;

    // ── Column headers ──
    const colProduct = margin;
    const colPeriod = margin + 70;
    const colBudget = colRight;

    doc.setFont(font, 'bold');
    doc.setFontSize(8);
    doc.setTextColor(138, 145, 153);
    doc.text('Product', colProduct, y);
    doc.text('Period', colPeriod, y);
    doc.text('Gross Budget', colBudget, y, { align: 'right' });
    y += 3;

    doc.setDrawColor(228, 232, 234);
    doc.setLineWidth(0.3);
    doc.line(margin, y, colRight, y);
    y += 5;

    // ── Product rows ──
    doc.setFont(font, 'normal');
    doc.setFontSize(9);

    for (const product of products) {
        if (y > 270) {
            doc.addPage();
            y = 16;
        }
        const period = product.periods[product.periods.length - 1];

        doc.setFont(font, 'bold');
        doc.setTextColor(35, 47, 52);
        doc.text(product.name, colProduct, y, { maxWidth: 60 });

        doc.setFont(font, 'normal');
        doc.setTextColor(90, 100, 110);
        doc.text(formatPeriodDate(period.period), colPeriod, y, { maxWidth: 55 });

        doc.setFont(font, 'normal');
        doc.setTextColor(35, 47, 52);
        doc.text(formatCurrency(period.grossBudget), colBudget, y, { align: 'right' });

        y += 6;

        doc.setDrawColor(243, 245, 246);
        doc.line(margin, y - 1, colRight, y - 1);
    }

    // ── Footer total ──
    y += 2;
    doc.setDrawColor(45, 58, 63);
    doc.setLineWidth(0.5);
    doc.line(margin, y, colRight, y);
    y += 5;
    doc.setFont(font, 'bold');
    doc.setFontSize(10);
    doc.setTextColor(35, 47, 52);
    doc.text('Total', colProduct, y);
    doc.text(formatCurrency(totalBudget), colBudget, y, { align: 'right' });

    const fileName = dealerName
        ? `contract_${dealerName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
        : 'contract_details.pdf';
    doc.save(fileName);
}
